import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, Progress, SimpleGrid, Stack, Text, Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { IconManual } from '../../components/icons/CncIcons';
import { useAuth, isAdmin } from '../../store/auth';
import { useLessons } from '../../lessons/store';
import { LESSONS, GROUP_TITLES, GROUP_ORDER } from '../../lessons/catalog';
import type { Lesson } from '../../lessons/types';
import { getPracticeData, cleanupPracticeData, totalPractice, PRACTICE_PREFIX, type PracticeCounts } from '../../lessons/practice';

function LessonCard({ lesson }: { lesson: Lesson }) {
  const navigate = useNavigate();
  const { progress, start } = useLessons();
  const p = progress[lesson.id];
  const completed = p?.status === 'completed';
  const inProgress = p?.status === 'in_progress';

  function begin() {
    start(lesson);
    navigate(lesson.startPath);
  }

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="xs" justify="space-between" style={{ height: '100%' }}>
        <div>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Text fw={700}>{lesson.title}</Text>
            {completed
              ? <Badge color="green" variant="filled">✓ Done</Badge>
              : inProgress
                ? <Badge color="orange" variant="light">Started</Badge>
                : <Badge color="gray" variant="light">~{lesson.minutes} min</Badge>}
          </Group>
          <Text size="sm" c="dimmed" mt={4}>{lesson.description}</Text>
          {lesson.practiceNote && (
            <Text size="xs" c="orange.7" mt={6}>{lesson.practiceNote}</Text>
          )}
        </div>
        <Button
          variant={completed ? 'default' : 'light'}
          size="sm"
          onClick={begin}
          data-tour={`lesson-start-${lesson.id}`}
        >
          {completed ? 'Do it again' : inProgress ? 'Restart lesson' : 'Start lesson'}
        </Button>
      </Stack>
    </Card>
  );
}

function PracticeDataCard() {
  const [counts, setCounts] = useState<PracticeCounts | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try { setCounts(await getPracticeData()); } catch { /* non-fatal */ }
  }
  useEffect(() => { refresh(); }, []);

  const total = counts ? totalPractice(counts) : 0;

  function confirmCleanup() {
    modals.openConfirmModal({
      title: 'Delete all practice data?',
      children: (
        <Text size="sm">
          This removes every record whose name starts with <b>{PRACTICE_PREFIX}&nbsp;</b>
          (all caps, followed by a space) — the customers, orders, machines, materials and
          technicians created by lessons. Real data is not touched, even if its name contains
          the word “training”. Practice equipment is only deleted when an admin runs this.
          This cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete practice data', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setBusy(true);
        try {
          const deleted = await cleanupPracticeData();
          const parts = Object.entries(deleted)
            .filter(([k, v]) => k !== 'prefix' && k !== 'equipmentSkipped' && Number(v) > 0)
            .map(([k, v]) => `${v} ${k}`);
          const skipped = deleted.equipmentSkipped ?? 0;
          notifications.show({
            title: 'Practice data removed 🧹',
            message: (parts.length ? `Deleted: ${parts.join(', ')}.` : 'There was nothing to delete.')
              + (skipped > 0 ? ` ${skipped} practice equipment left — ask an admin to run the cleanup (equipment deletion is admin-only).` : ''),
            color: 'green',
          });
          await refresh();
        } catch (e: any) {
          notifications.show({ message: `Cleanup failed: ${e.message ?? e}`, color: 'red' });
        } finally {
          setBusy(false);
        }
      },
    });
  }

  return (
    <Card withBorder radius="md" padding="md" data-tour="lessons-cleanup-card">
      <Group justify="space-between" wrap="wrap">
        <div>
          <Text fw={700}>Practice data</Text>
          <Text size="sm" c="dimmed">
            {counts === null
              ? 'Counting practice records…'
              : total === 0
                ? `No “${PRACTICE_PREFIX}…” records right now — all clean.`
                : `Currently: ${counts.customers} customers, ${counts.orders} orders, ${counts.equipment} equipment, ${counts.consumables} consumables, ${counts.accessories} accessories, ${counts.technicians} technicians.`}
          </Text>
        </div>
        <Button color="red" variant="light" onClick={confirmCleanup} loading={busy} disabled={counts !== null && total === 0} data-tour="lessons-cleanup">
          Clean up practice data
        </Button>
      </Group>
    </Card>
  );
}

export default function LessonsPage() {
  const { me } = useAuth();
  const admin = isAdmin(me?.role);
  const { progress, progressLoaded, loadProgress, active } = useLessons();

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const lessons = LESSONS.filter((l) => !l.adminOnly || admin);
  const done = lessons.filter((l) => progress[l.id]?.status === 'completed').length;

  return (
    <Stack>
      <Group gap={10}>
        <IconManual size={26} />
        <Title order={2}>Lessons</Title>
      </Group>
      <Text c="dimmed" maw={760}>
        Interactive, hands-on lessons that drive the real CYCLOTRON — each one highlights
        the exact button to press, step by step, until you have done the thing for real.
        Records you create are named “{PRACTICE_PREFIX}…” and can be removed with one click below.
      </Text>

      {progressLoaded && (
        <div style={{ maxWidth: 420 }}>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={600}>Your progress</Text>
            <Text size="sm" c="dimmed">{done} / {lessons.length} lessons</Text>
          </Group>
          <Progress value={lessons.length ? (done / lessons.length) * 100 : 0} color={done === lessons.length ? 'green' : 'orange'} />
        </div>
      )}

      {active && (
        <Alert color="orange" variant="light">
          A lesson is currently running — follow the highlighted steps, or use its Exit button to stop.
        </Alert>
      )}

      <Alert color="gray" variant="light">
        Tip: don’t reload the browser mid-lesson — a reload stops the lesson (your app data
        is safe; just restart the lesson from here). Lessons marked with an orange note create
        practice records.
      </Alert>

      {GROUP_ORDER.map((g) => {
        const group = lessons.filter((l) => l.group === g);
        if (group.length === 0) return null;
        return (
          <div key={g}>
            <Title order={4} mb="xs" mt="sm">{GROUP_TITLES[g]}</Title>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {group.map((l) => <LessonCard key={l.id} lesson={l} />)}
            </SimpleGrid>
          </div>
        );
      })}

      <Title order={4} mt="sm">Housekeeping</Title>
      <PracticeDataCard />
    </Stack>
  );
}
